<script>
  import { onMount, afterUpdate } from 'svelte';
  import HelpLayout from '../components/HelpLayout.svelte';

  export let params = {};

  // Resolve @video players to short-lived signed URLs from the login-gated
  // endpoint. Runs after each render; each slug is only fetched once.
  const hydratedVideos = new Set();
  async function hydrateVideos() {
    const els = document.querySelectorAll('video[data-help-video]');
    for (const el of els) {
      const slug = el.getAttribute('data-help-video');
      if (!slug || el.getAttribute('src') || hydratedVideos.has(slug)) continue;
      hydratedVideos.add(slug);
      const msg = document.querySelector(`[data-help-video-msg="${slug}"]`);
      try {
        const r = await fetch(`/api/help/video-url?slug=${encodeURIComponent(slug)}`, { credentials: 'include' });
        if (r.ok) {
          const data = await r.json();
          if (data.url) { el.setAttribute('src', data.url); el.load && el.load(); }
        } else if (r.status === 401) {
          if (msg) msg.textContent = 'Please sign in to watch this video.';
        } else if (r.status === 404) {
          if (msg) msg.textContent = 'Video coming soon.';
        }
      } catch (_e) {
        if (msg) msg.textContent = 'Could not load video.';
      }
    }
  }
  afterUpdate(hydrateVideos);

  let docContent = '';
  let docTitle = '';
  let docCategory = '';
  let docSlug = '';
  let requiresAuth = false;
  let loading = true;
  let error = null;

  // Use params from the route
  $: if (params.category && params.slug) {
    loadDoc(params.category, params.slug);
  } else {
    // Fallback: try to extract from URL if params not available
    const pathParts = window.location.pathname.split('/').filter(part => part);
    if (pathParts.length >= 3 && pathParts[0] === 'help') {
      const category = pathParts[1];
      const slug = pathParts[2];
      loadDoc(category, slug);
    }
  }

  async function loadDoc(category, slug) {
    loading = true;
    error = null;
    
    try {
      const url = `/content/help/${category}/${slug}.md`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Document not found: ${response.status}`);
      }

      const content = await response.text();

      // Parse frontmatter if present (tolerate CRLF line endings)
      const frontmatterMatch = content.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/);
      let frontmatter = {};
      let body = content;

      if (frontmatterMatch) {
        const frontmatterStr = frontmatterMatch[1];
        body = frontmatterMatch[2].trim();

        // Parse YAML frontmatter
        const lines = frontmatterStr.split(/\r?\n/);
        lines.forEach(line => {
          const [key, value] = line.split(':').map(s => s.trim());
          if (key && value) {
            frontmatter[key] = value.replace(/^["']|["']$/g, ''); // Remove quotes
            if (key === 'requiresAuth') {
              frontmatter[key] = value.toLowerCase() === 'true';
            }
          }
        });
      }

      docTitle = frontmatter.title || slug.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
      requiresAuth = frontmatter.requiresAuth || false;
      docContent = body;
      docCategory = category;
      docSlug = slug;
      loading = false;

    } catch (err) {
      console.error('Error loading document:', err);
      error = err.message;
      loading = false;
    }
  }

  function renderMarkdown(content) {
    if (!content) return '';
    
    let html = content;

    // Loom video embeds — a line `@loom: https://www.loom.com/share/<id>` (or a
    // bare id) becomes a responsive 16:9 player. Authors just paste the share link.
    html = html.replace(/^@loom:?\s+(\S+).*$/gim, (_m, ref) => {
      const m = String(ref).match(/[0-9a-fA-F]{20,}/);
      const id = m ? m[0] : ref;
      return '<div class="relative my-6 rounded-lg overflow-hidden shadow-md bg-gray-100" style="padding-bottom:56.25%;height:0;">'
        + '<iframe src="https://www.loom.com/embed/' + id + '" class="absolute top-0 left-0 w-full h-full" style="border:0" allowfullscreen></iframe></div>';
    });

    // Self-hosted secure video: a line `@video: <slug>` renders a player whose
    // source is fetched from a login-gated endpoint after mount (see hydrateVideos).
    html = html.replace(/^@video:?\s+(\S+).*$/gim, (_m, slug) => {
      const s = String(slug).toLowerCase().replace(/[^a-z0-9._-]/g, '-');
      return '<div class="my-6" data-help-video-wrap="' + s + '">'
        + '<video controls preload="metadata" playsinline data-help-video="' + s + '" '
        + 'class="w-full rounded-lg shadow-md bg-black" style="aspect-ratio:16/9">'
        + 'Your browser does not support video playback.</video>'
        + '<div class="text-sm text-gray-400 mt-1" data-help-video-msg="' + s + '"></div></div>';
    });

    // Convert headers
    html = html.replace(/^#### (.*$)/gim, '<h4 class="text-lg font-semibold text-gray-900 mt-4 mb-2">$1</h4>');
    html = html.replace(/^### (.*$)/gim, '<h3 class="text-xl font-semibold text-gray-900 mt-6 mb-3">$1</h3>');
    html = html.replace(/^## (.*$)/gim, '<h2 class="text-2xl font-bold text-gray-900 mt-8 mb-4">$1</h2>');
    html = html.replace(/^# (.*$)/gim, '<h1 class="text-3xl font-bold text-gray-900 mb-6">$1</h1>');
    
    // Convert bold and italic
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>');
    html = html.replace(/\*(.*?)\*/g, '<em class="italic">$1</em>');
    
    // Convert inline code
    html = html.replace(/`(.*?)`/g, '<code class="px-1.5 py-0.5 bg-gray-100 text-sm rounded">$1</code>');
    
    // Convert images (must come before links)
    html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="max-w-full h-auto rounded-lg shadow-md my-4" />');
    
    // Convert links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-blue-600 hover:text-blue-800 underline">$1</a>');
    
    // Convert lists
    const lines = html.split('\n');
    let inList = false;
    let inOrderedList = false;
    let result = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Unordered list - handle both regular hyphens and escaped hyphens (\-)
      if (line.match(/^\\?[-*+] /)) {
        if (!inList) {
          result.push('<ul class="list-disc list-inside space-y-2 my-4">');
          inList = true;
        }
        result.push('<li class="text-gray-700">' + line.replace(/^\\?[-*+] /, '') + '</li>');
      }
      // Ordered list
      else if (line.match(/^\d+\. /)) {
        if (!inOrderedList) {
          result.push('<ol class="list-decimal list-inside space-y-2 my-4">');
          inOrderedList = true;
        }
        result.push('<li class="text-gray-700">' + line.replace(/^\d+\. /, '') + '</li>');
      }
      else {
        if (inList) {
          result.push('</ul>');
          inList = false;
        }
        if (inOrderedList) {
          result.push('</ol>');
          inOrderedList = false;
        }
        
        // Regular paragraphs (skip block-level HTML: headings, lists, embeds, etc.)
        if (line.trim() !== '' && !line.match(/^\s*<(h[1-6]|ul|ol|li|div|iframe|figure|table|blockquote|pre)\b/i)) {
          result.push('<p class="text-gray-700 leading-relaxed my-4">' + line + '</p>');
        } else {
          result.push(line);
        }
      }
    }
    
    if (inList) result.push('</ul>');
    if (inOrderedList) result.push('</ol>');
    
    return result.join('\n');
  }
</script>

<HelpLayout category={docCategory} slug={docSlug} {requiresAuth}>
  {#if loading}
    <div class="text-center py-12">
      <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      <p class="mt-4 text-gray-600">Loading document...</p>
    </div>
  {:else if error}
    <div class="text-center py-12">
      <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z"></path>
        </svg>
      </div>
      <h2 class="text-xl font-semibold text-gray-900 mb-2">Document Not Found</h2>
      <p class="text-gray-600 mb-4">{error}</p>
      <a href="/help" class="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium">
        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
        </svg>
        Back to Help Center
      </a>
    </div>
  {:else}
    <div class="prose prose-lg max-w-none">
      {@html renderMarkdown(docContent)}
    </div>
  {/if}
</HelpLayout>
